import { useEffect, useState } from 'react';

interface InterestIndicatorProps {
  interestScore?: number; // 0-100
  className?: string;
  isLoading?: boolean;
}

export default function InterestIndicator({
  interestScore: interestScoreProp = 0,
  className = '',
  isLoading = false
}: InterestIndicatorProps) {
  // Track previous score to create animation effects for big changes
  const [prevScore, setPrevScore] = useState<number | null>(null);
  const [isFlashing, setIsFlashing] = useState(false);

  const interestScore = typeof interestScoreProp === 'number' ? interestScoreProp : 0;
  
  // Check for significant score changes to trigger animations
  useEffect(() => {
    if (prevScore !== null) {
      const scoreDiff = Math.abs(interestScore - prevScore);
      
      // If big swing in score, show animation effect
      if (scoreDiff > 15) {
        setIsFlashing(true);
        setTimeout(() => setIsFlashing(false), 1500);
      }
    }
    
    setPrevScore(interestScore);
  }, [interestScore, prevScore]);
  
  // Get interest level details based on score
  const getInterestDetails = (score: number) => {
    if (score < 20) {
      return {
        label: 'Not Interested',
        gradient: 'from-blue-600 to-blue-400',
        textColor: 'text-blue-500',
        glow: 'shadow-blue-500/20',
        icon: (
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Bored face */}
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
            <circle cx="8" cy="10" r="1" fill="currentColor"/>
            <circle cx="16" cy="10" r="1" fill="currentColor"/>
            <line x1="8" y1="15" x2="16" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        )
      };
    }
    if (score < 40) {
      return {
        label: 'Polite',
        gradient: 'from-indigo-500 to-blue-400',
        textColor: 'text-indigo-500',
        glow: 'shadow-indigo-500/20',
        icon: (
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Neutral face */}
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
            <circle cx="8" cy="10" r="1" fill="currentColor"/>
            <circle cx="16" cy="10" r="1" fill="currentColor"/>
            <line x1="8" y1="15" x2="16" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )
      };
    }
    if (score < 60) {
      return {
        label: 'Curious',
        gradient: 'from-amber-500 to-orange-400',
        textColor: 'text-amber-500',
        glow: 'shadow-amber-500/20',
        icon: (
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Face with raised eyebrows (flirtatious) */}
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
            <circle cx="8" cy="10" r="1" fill="currentColor"/>
            <circle cx="16" cy="10" r="1" fill="currentColor"/>
            <path d="M8.5 14.5C8.5 14.5 9.5 17 12 17C14.5 17 15.5 14.5 15.5 14.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M7 8C7 8 8 6 10 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M17 8C17 8 16 6 14 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )
      };
    }
    if (score < 80) {
      return {
        label: 'Interested',
        gradient: 'from-orange-600 to-rose-500',
        textColor: 'text-orange-600',
        glow: 'shadow-orange-600/20',
        icon: (
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Heart */}
            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" stroke="currentColor" strokeWidth="2" fill="currentColor" fillOpacity="0.2"/>
          </svg>
        )
      };
    }
    return {
      label: 'Very Interested',
      gradient: 'from-red-600 to-pink-500',
      textColor: 'text-red-600',
      glow: 'shadow-red-600/20',
      icon: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          {/* Water drop (representing climax) - slimmer version */}
          <path d="M12 3l3.5 5a7 7 0 1 1-7 0L12 3z" stroke="currentColor" strokeWidth="2" fill="currentColor" fillOpacity="0.2"/>
        </svg>
      )
    };
  };
  
  const interest = getInterestDetails(interestScore);
  
  // Flash class for significant changes
  const flashClass = isFlashing 
    ? 'animate-pulse transition-all duration-1000' 
    : 'transition-all duration-500';
  
  return (
    <div 
      className={`flex items-center ${className} ${flashClass} gap-1`} 
      title={`Interest level: ${Math.round(interestScore)}%`}
    >
      {/* Interest level pill */}
      <div className={`relative flex items-center w-full h-[26px] rounded-full bg-background/50 backdrop-blur-sm border border-border/30 shadow-sm overflow-hidden ${isLoading ? 'animate-pulse' : ''}`}>
        {/* Progress bar with gradient */}
        <div 
          className={`absolute left-0 top-0 bottom-0 transition-all duration-500 bg-gradient-to-r ${interest.gradient} ${isFlashing ? 'shadow-lg' : ''} ${isLoading ? 'opacity-50' : ''}`}
          style={{ 
            width: `${interestScore}%`,
            boxShadow: isFlashing ? `0 0 8px 1px rgba(255, 255, 255, 0.4)` : 'none'
          }}
        />
        
        {/* Icon and score container */}
        <div className="w-full h-full flex items-center justify-between px-2 z-10">
          <div className={`flex items-center gap-1 ${interest.textColor} font-medium text-xs ${isLoading ? 'opacity-50' : ''}`}>
            <span className={`rounded-full p-[3px] bg-background/70 ${interest.glow}`}>
              {interest.icon}
            </span>
          </div>
          <span className={`font-medium text-[10px] bg-background/70 rounded-full px-1.5 py-0.5 shadow-sm ${isLoading ? 'opacity-50' : ''}`}>
            {isLoading ? '...' : `${Math.round(interestScore)}%`}
          </span>
        </div>
      </div>
    </div>
  );
} 